import { useEffect, useState } from "react";
import PropTypes from "prop-types";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  LinearProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tabs,
  Tab,
  Typography,
} from "@mui/material";
import { Close, LocalParking } from "@mui/icons-material";
import {
  inactiveParking,
  parkingActivity,
  parkingDate,
  parkingDuration,
  parkingView,
} from "../../model/parkingModel";
const stateLabels = {
  "COMERCIO CON FALLO": "Fallo total",
  "FALLA PARCIAL": "Fallo parcial",
  OPERATIVO: "Operativo",
  "SIN TERMINALES": "Sin terminales",
  "SIN DATOS": "Sin datos",
};
const palettes = {
  error: ["#b42318", "#fff1f0"],
  warning: ["#885515", "#fff6e6"],
  success: ["#217346", "#edf8f0"],
  info: ["#526579", "#eef3f8"],
};
const tableSx = {
  minWidth: 900,
  "& th": { bgcolor: "#193e54", color: "#fff", fontSize: ".75rem", fontWeight: 700 },
  "& td": { color: "#243b53", fontSize: ".85rem", py: 1.75, overflowWrap: "anywhere" },
};
function StatusBadge({ row }) {
  const [color, bgcolor] = palettes[row.severity] || palettes.info;
  return (
    <Chip
      size="small"
      label={stateLabels[row.estado_general] || row.estado_general}
      sx={{ color, bgcolor, fontWeight: 600 }}
    />
  );
}
StatusBadge.propTypes = { row: PropTypes.object.isRequired };
function CommerceDetails({ row, onClose }) {
  return (
    <Dialog
      open={Boolean(row)}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      aria-labelledby="parking-detail-title"
      PaperProps={{ sx: { borderRadius: 3, maxHeight: "92vh", m: { xs: 1, sm: 3 } } }}
    >
      <DialogTitle
        component="div"
        sx={{ display: "flex", gap: 1.5, alignItems: "center", bgcolor: "#193e54", px: 3, py: 2 }}
      >
        <LocalParking sx={{ color: "#fff" }} />
        <Box sx={{ flex: 1 }}>
          <Typography
            id="parking-detail-title"
            component="h2"
            sx={{ color: "#fff", fontSize: "1rem", fontWeight: 700 }}
          >
            {row?.comercio}
          </Typography>
          <Typography sx={{ color: "#dbe8f2", fontSize: ".75rem" }}>
            Detalle de terminales y ciclos
          </Typography>
        </Box>
        <IconButton
          aria-label="Cerrar detalle del comercio"
          onClick={onClose}
          sx={{ color: "#fff" }}
        >
          <Close />
        </IconButton>
      </DialogTitle>
      {row && (
        <DialogContent sx={{ p: { xs: 2, sm: 3 }, bgcolor: "#f5f7fa" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap", my: 2.5 }}>
            <StatusBadge row={row} />
            <Chip size="small" label={row.tipo_comercio} />
            <Chip size="small" label={`Integración: ${row.tipo_integracion}`} />
          </Box>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "repeat(3,1fr)" },
              gap: 2,
              mb: 2.5,
            }}
          >
            {[
              [
                "Terminales operativas",
                row.total_terminals
                  ? `${row.terminals_sin_novedad} de ${row.total_terminals}`
                  : "Sin terminales",
              ],
              ["Ciclos abiertos", row.ciclos_abiertos ?? "Sin datos"],
              ["Actividad de ciclos", parkingActivity(row)],
            ].map(([label, value]) => (
              <Box
                key={label}
                sx={{ p: 2, bgcolor: "#fff", border: "1px solid #dce5ed", borderRadius: 2 }}
              >
                <Typography sx={{ fontSize: ".75rem", color: "#526579", mb: 0.5 }}>
                  {label}
                </Typography>
                <Typography sx={{ fontSize: ".9rem", color: "#243b53", fontWeight: 600 }}>
                  {value}
                </Typography>
              </Box>
            ))}
          </Box>
          {inactiveParking(row) && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              Este comercio lleva más de 3 días sin reportar ciclos.
            </Alert>
          )}
          <Typography
            component="h3"
            sx={{ fontWeight: 700, fontSize: ".9rem", color: "#193e54", mb: 1.5 }}
          >
            Todas las terminales ({row.total_terminals})
          </Typography>
          {row.terminals.length ? (
            <TableContainer
              component={Paper}
              variant="outlined"
              sx={{ maxHeight: "50vh", borderRadius: 2, boxShadow: "none" }}
            >
              <Table
                stickyHeader
                size="small"
                aria-label="Terminales del comercio"
                sx={{ ...tableSx, minWidth: 700 }}
              >
                <TableHead sx={{ display: "table-header-group", p: 0 }}>
                  <TableRow>
                    {[
                      "Terminal",
                      "Estado",
                      "Última entrada · Bogotá",
                      "Tiempo inactivo",
                      "Promedio entre entradas",
                    ].map((label) => (
                      <TableCell key={label}>{label}</TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {row.terminals.map((terminal, index) => (
                    <TableRow key={`${terminal.terminalname}-${index}`}>
                      <TableCell sx={{ fontWeight: 600 }}>
                        {terminal.terminalname || "Sin nombre"}
                      </TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={terminal.estado_terminal}
                          sx={{
                            fontSize: ".7rem",
                            ...({
                              "SIN NOVEDAD": { color: "#217346", bgcolor: "#edf8f0" },
                              "POSIBLE FALLO": { color: "#885515", bgcolor: "#fff6e6" },
                              "CON NOVEDAD": { color: "#b42318", bgcolor: "#fff1f0" },
                            }[terminal.estado_terminal] || {
                              color: "#526579",
                              bgcolor: "#eef3f8",
                            }),
                          }}
                        />
                      </TableCell>
                      <TableCell>{parkingDate(terminal.ultima_entrada)}</TableCell>
                      <TableCell>{parkingDuration(terminal.minutos_desde_ultima)}</TableCell>
                      <TableCell>{parkingDuration(terminal.promedio_minutos)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Alert severity="info">
              No se recibieron terminales para este comercio. Esto no confirma que esté operativo.
            </Alert>
          )}
        </DialogContent>
      )}
      <DialogActions sx={{ px: 3, py: 1.5, borderTop: "1px solid #dce5ed" }}>
        <Button
          variant="outlined"
          onClick={onClose}
          sx={{ color: "#193e54", borderColor: "#c6d3df" }}
        >
          Cerrar
        </Button>
      </DialogActions>
    </Dialog>
  );
}
CommerceDetails.propTypes = { row: PropTypes.object, onClose: PropTypes.func.isRequired };
export default function TableParking({
  comercios,
  loading,
  error,
  warning,
  onRefresh,
  resetKey,
  activeView = "all",
  onViewChange,
}) {
  const tab = activeView;
  const [page, setPage] = useState(0),
    [pageSize, setPageSize] = useState(25),
    [selected, setSelected] = useState(null);
  useEffect(() => {
    setPage(0);
  }, [resetKey, tab]);
  const groups = Object.fromEntries(
    ["all", "fail", "open", "inactive"].map((key) => [key, parkingView(comercios, key)])
  );
  const rows = groups[tab];
  const safePage = Math.min(page, Math.max(0, Math.ceil(rows.length / pageSize) - 1));
  return (
    <>
      <CommerceDetails
        row={comercios.find((r) => r.id === selected) || null}
        onClose={() => setSelected(null)}
      />
      {warning && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {warning}
        </Alert>
      )}
      {error && (
        <Alert
          severity="error"
          action={
            <Button onClick={onRefresh} disabled={loading}>
              Reintentar
            </Button>
          }
          sx={{ mb: 2 }}
        >
          {error}
          {comercios.length ? " Se conservan los últimos datos disponibles." : ""}
        </Alert>
      )}
      <Paper variant="outlined" sx={{ borderRadius: 2, overflow: "hidden" }}>
        <Tabs
          value={tab}
          onChange={(_, value) => onViewChange(value)}
          variant="scrollable"
          scrollButtons="auto"
          aria-label="Vistas de Auditoría Parking"
          sx={{
            borderBottom: "1px solid #dce5ed",
            "& .MuiTab-root": { color: "#526579" },
            "& .Mui-selected": { color: "#fff !important" },
            "& .MuiTabs-indicator": { bgcolor: "#193e54" },
          }}
        >
          {[
            ["all", "Todos"],
            ["fail", "Fallos"],
            ["open", "Ciclos abiertos"],
            ["inactive", "Sin actividad > 3 días"],
          ].map(([key, label]) => (
            <Tab key={key} value={key} label={`${label} (${groups[key].length})`} />
          ))}
        </Tabs>
        {loading && <LinearProgress aria-label="Cargando Auditoría Parking" />}
        <TableContainer sx={{ maxHeight: "65vh", borderRadius: 0, boxShadow: "none" }}>
          <Table stickyHeader size="small" aria-label="Auditoría de comercios" sx={tableSx}>
            <TableHead sx={{ display: "table-header-group", p: 0 }}>
              <TableRow>
                {[
                  "Comercio / Integración",
                  "Estado operativo",
                  "Terminales operativas",
                  "Ciclos abiertos",
                  "Actividad de ciclos",
                  "Detalle",
                ].map((label) => (
                  <TableCell key={label}>{label}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.slice(safePage * pageSize, (safePage + 1) * pageSize).map((row) => (
                <TableRow
                  key={row.id}
                  hover
                  sx={{
                    bgcolor: {
                      error: "#fff5f3",
                      warning: "#fffaf0",
                      success: "#f0f8f3",
                      info: "#f4f7fb",
                    }[row.severity],
                    "& td:first-of-type": {
                      borderLeft: `4px solid ${(palettes[row.severity] || palettes.info)[0]}`,
                    },
                    "&.MuiTableRow-hover:hover": {
                      bgcolor: {
                        error: "#fce7e3",
                        warning: "#fff0d4",
                        success: "#e1f1e6",
                        info: "#e7edf5",
                      }[row.severity],
                    },
                  }}
                >
                  <TableCell>
                    <Typography sx={{ fontWeight: 600, fontSize: ".9rem", color: "#243b53" }}>
                      {row.comercio}
                    </Typography>
                    <Typography sx={{ fontSize: ".75rem", color: "#526579" }}>
                      {row.tipo_comercio} · {row.tipo_integracion}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <StatusBadge row={row} />
                  </TableCell>
                  <TableCell>
                    {row.total_terminals
                      ? `${row.terminals_sin_novedad} de ${row.total_terminals}`
                      : "Sin terminales"}
                    {row.terminals_sin_datos > 0 && (
                      <Typography sx={{ fontSize: ".72rem", color: "#526579" }}>
                        {row.terminals_sin_datos} sin datos
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>
                    <Chip
                      size="small"
                      label={row.ciclos_abiertos?.toLocaleString("es-CO") ?? "Sin datos"}
                      sx={{
                        minWidth: 42,
                        fontWeight: 700,
                        color: row.ciclos_abiertos > 0 ? "#185d8e" : "#526579",
                        bgcolor: row.ciclos_abiertos > 0 ? "#dcecff" : "#e9eef3",
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography
                      sx={{
                        fontSize: ".85rem",
                        fontWeight: inactiveParking(row) ? 600 : 400,
                        color: inactiveParking(row) ? "#885515" : "#243b53",
                      }}
                    >
                      {parkingActivity(row)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Button
                      size="small"
                      onClick={() => setSelected(row.id)}
                      aria-label={`Ver detalle de ${row.comercio}`}
                    >
                      Ver
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
              {!rows.length && (
                <TableRow>
                  <TableCell colSpan={6}>
                    <Box sx={{ textAlign: "center", p: 4 }}>
                      {loading
                        ? "Cargando comercios…"
                        : "No hay comercios que coincidan con esta vista y los filtros."}
                    </Box>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={rows.length}
          page={safePage}
          onPageChange={(_, value) => setPage(value)}
          rowsPerPage={pageSize}
          onRowsPerPageChange={(e) => {
            setPageSize(Number(e.target.value));
            setPage(0);
          }}
          rowsPerPageOptions={[10, 25, 50, 100, 200]}
          labelRowsPerPage="Filas por página"
          labelDisplayedRows={({ from, to, count }) => `${from}–${to} de ${count} comercios`}
        />
      </Paper>
    </>
  );
}
TableParking.propTypes = {
  comercios: PropTypes.array.isRequired,
  loading: PropTypes.bool,
  error: PropTypes.string,
  warning: PropTypes.string,
  onRefresh: PropTypes.func.isRequired,
  resetKey: PropTypes.string,
  activeView: PropTypes.string,
  onViewChange: PropTypes.func.isRequired,
};
